"use client";

import { createElement, useEffect, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { useFormatter } from "@/hooks/use-formatter"
import {
  IconAlertTriangle,
  IconBellRinging,
  IconCalendarEvent,
  IconClock,
  IconDoor,
  IconMail,
  IconNotes,
  IconPhone,
  IconStethoscope,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth-store";
import { formField } from "@/lib/appointment-presets";
import { getAppointmentReminders } from "@/lib/client-reminders-api";
import { categoryIcon, getCategoryLabel } from "@/lib/appointment-categories";
import { cn } from "@/lib/utils";
import type { Appointment, AppointmentReminder } from "@/types";

const LABEL_CLS =
  "text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground";

function Row({ icon, children, className }: { icon: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-start gap-2.5 text-sm", className)}>
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

interface AppointmentSheetViewProps {
  appointment: Appointment;
  remindersEnabled: boolean;
  /** Bumped by the host after a save or a send so the list refetches. */
  remindersVersion: number;
  onSendReminder: () => void;
}

/**
 * 86-01: the sheet's VIEW body — a readout of everything the row carries,
 * ordered the way the desk reads it: when, who, patient, notes, check-in
 * stamps, reminders.
 */
export function AppointmentSheetView({
  appointment: appt,
  remindersEnabled,
  remindersVersion,
  onSendReminder,
}: AppointmentSheetViewProps) {
  const t = useTranslations("appointments.sheet");
  const ta = useTranslations("appointments");
  const tv = useTranslations("visitCategorization");
  const format = useFormatter();
  const eventTypesEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.typed_events_enabled === true
  );
  // Appointments rebuild 88: the category-filter flag became a form preset.
  const categoryEnabled = useAuthStore(
    (s) => formField(s.user?.appointment_presets, "category") === "shown"
  );
  const roomsEnabled = useAuthStore(
    (s) => (s.user?.feature_flags as { rooms_enabled?: boolean } | undefined)?.rooms_enabled ?? false
  );

  const start = new Date(appt.scheduled_at);
  const end = new Date(appt.ends_at ?? appt.end_at);
  const lead = appt.lead;
  const isCall = appt.type === "lead_call";

  const [reminders, setReminders] = useState<AppointmentReminder[] | null>(null);
  useEffect(() => {
    if (!remindersEnabled) return;
    let cancelled = false;
    getAppointmentReminders(appt.id)
      .then((rows) => {
        if (!cancelled) setReminders(rows);
      })
      .catch(() => {
        if (!cancelled) setReminders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [appt.id, remindersEnabled, remindersVersion]);

  const stamp = (iso: string | null | undefined) =>
    iso ? format.dateTime(new Date(iso), { hour: "2-digit", minute: "2-digit" }) : null;
  const checkIn = [
    appt.time_arrived ? ta("appointmentForm.arrivedAt", { time: stamp(appt.time_arrived) ?? "" }) : null,
    appt.time_seated ? ta("appointmentForm.seatedAt", { time: stamp(appt.time_seated) ?? "" }) : null,
    appt.time_dismissed ? ta("appointmentForm.dismissedAt", { time: stamp(appt.time_dismissed) ?? "" }) : null,
  ].filter((s): s is string => !!s);

  const reminderStatusLabel = (status: AppointmentReminder["status"]) => {
    switch (status) {
      case "pending":
        return t("reminderStatus.pending");
      case "sent":
        return t("reminderStatus.sent");
      case "delivered":
        return t("reminderStatus.delivered");
      case "read":
        return t("reminderStatus.read");
      case "failed":
        return t("reminderStatus.failed");
      default:
        return t("reminderStatus.cancelled");
    }
  };

  const visitLabel = appt.visit
    ? `${tv.has(`visit.${appt.visit.visit_number}`) ? tv(`visit.${appt.visit.visit_number}`) : tv("visit.generic", { number: appt.visit.visit_number })}${appt.visit.purpose ? ` — ${tv.has(`purposes.${appt.visit.purpose}`) ? tv(`purposes.${appt.visit.purpose}`) : appt.visit.purpose}` : ""}`
    : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5" data-slot="appointment-sheet-view">
      {/* When */}
      <section className="flex flex-col gap-2">
        <span className={LABEL_CLS}>{t("when")}</span>
        <Row icon={<IconCalendarEvent className="size-4" />}>
          {format.dateTime(start, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </Row>
        <Row icon={<IconClock className="size-4" />}>
          <span className="tabular-nums">
            {format.dateTime(start, { hour: "numeric", minute: "2-digit" })}
            {" – "}
            {format.dateTime(end, { hour: "numeric", minute: "2-digit" })}
          </span>
          <span className="text-muted-foreground">
            {" · "}
            {t("minutes", { count: appt.duration_minutes })}
          </span>
        </Row>
      </section>

      {/* Who */}
      <section className="flex flex-col gap-2">
        <span className={LABEL_CLS}>{t("who")}</span>
        {appt.user?.full_name && (
          <Row icon={<IconUser className="size-4" />}>
            {appt.user.full_name}
            {appt.care_team?.assignee_off_team && appt.care_team.doctor?.full_name && (
              <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-500">
                <IconAlertTriangle className="size-3.5" aria-hidden />
                {ta("appointmentForm.careTeam.offTeam", { name: appt.care_team.doctor.full_name })}
              </span>
            )}
          </Row>
        )}
        {/* ArtDent 261005 — the other doctors working it. */}
        {!isCall && appt.co_doctors && appt.co_doctors.length > 0 && (
          <Row icon={<IconUsers className="size-4" />}>
            <span className="text-muted-foreground">{ta("appointmentForm.coDoctors.label")}: </span>
            {appt.co_doctors.map((doctor) => doctor.full_name).join(", ")}
          </Row>
        )}
        {roomsEnabled && !isCall && (
          <Row icon={<IconDoor className="size-4" />}>
            {appt.room ? (
              <span className="inline-flex items-center gap-2">
                {appt.room.color && (
                  <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: appt.room.color }} />
                )}
                {appt.room.name}
              </span>
            ) : (
              <span className="text-muted-foreground">{t("noRoom")}</span>
            )}
          </Row>
        )}
        {(categoryEnabled || eventTypesEnabled || visitLabel) && (
          <Row icon={isCall ? <IconPhone className="size-4" /> : createElement(categoryIcon(appt.category), { className: "size-4" })}>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {eventTypesEnabled && <span>{isCall ? t("typeCall") : t("typeVisit")}</span>}
              {categoryEnabled && appt.category && (
                <span className="text-muted-foreground">{getCategoryLabel(ta, appt.category)}</span>
              )}
              {visitLabel && <span className="text-muted-foreground">{visitLabel}</span>}
            </span>
          </Row>
        )}
      </section>

      {/* Patient */}
      {lead && (
        <section className="rounded-[10px] border bg-muted/40 p-3.5">
          <div className={cn("mb-1.5", LABEL_CLS)}>{t("patient")}</div>
          <div className="font-medium">{lead.full_name}</div>
          <div className="mt-1 space-y-1 text-sm text-muted-foreground">
            {lead.phone && (
              <div className="flex items-center gap-2">
                <IconPhone className="size-3.5 shrink-0" />
                <a href={`tel:${lead.phone}`} className="font-mono hover:text-foreground hover:underline">
                  {lead.phone}
                </a>
              </div>
            )}
            {lead.email && (
              <div className="flex items-center gap-2">
                <IconMail className="size-3.5 shrink-0" />
                <span className="truncate">{lead.email}</span>
              </div>
            )}
          </div>
          {lead.has_medical_alert && (
            <div className="mt-2 flex items-start gap-2 rounded-[8px] border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-[12.5px] text-destructive">
              <IconAlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
              <span>
                <span className="font-semibold">{t("medicalAlert")}</span>
                {lead.medical_alert ? ` — ${lead.medical_alert}` : ""}
              </span>
            </div>
          )}
        </section>
      )}

      {/* Notes */}
      {(appt.title || appt.description) && (
        <section className="flex flex-col gap-2">
          <span className={LABEL_CLS}>{t("notes")}</span>
          {appt.title && (
            <Row icon={<IconStethoscope className="size-4" />}>{appt.title}</Row>
          )}
          {appt.description && (
            <Row icon={<IconNotes className="size-4" />}>
              <p className="whitespace-pre-wrap text-muted-foreground">{appt.description}</p>
            </Row>
          )}
        </section>
      )}

      {/* Check-in stamps (any day) */}
      {checkIn.length > 0 && (
        <section className="flex flex-col gap-1.5">
          <span className={LABEL_CLS}>{t("checkIn")}</span>
          <p className="text-xs tabular-nums text-muted-foreground">{checkIn.join(" · ")}</p>
        </section>
      )}

      {/* Reminders */}
      {remindersEnabled && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className={LABEL_CLS}>{t("reminders")}</span>
            {lead?.phone ? (
              <Button type="button" variant="outline" size="sm" className="h-7" onClick={onSendReminder}>
                <IconBellRinging className="mr-1.5 size-3.5" />
                {t("sendNow")}
              </Button>
            ) : (
              <span className="text-[11px] text-muted-foreground">{t("noPhone")}</span>
            )}
          </div>
          {reminders === null ? (
            <p className="text-xs text-muted-foreground">…</p>
          ) : reminders.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t("remindersEmpty")}</p>
          ) : (
            <ul className="divide-y rounded-[10px] border text-[12.5px]">
              {reminders.slice(0, 5).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="min-w-0 truncate tabular-nums text-muted-foreground">
                    {format.dateTime(new Date(r.sent_at ?? r.scheduled_for ?? r.created_at), {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                    {r.is_manual ? ` · ${t("manual")}` : ""}
                    {r.channel === "email" ? ` · ${t("viaEmail")}` : ""}
                  </span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
                      r.status === "failed"
                        ? "bg-destructive/10 text-destructive"
                        : r.status === "pending"
                          ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                          : "bg-primary/10 text-primary"
                    )}
                  >
                    {reminderStatusLabel(r.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
